import type { Auth, User } from "firebase/auth";
import { useEffect, useState } from "react";
import * as api from "./api";
import { FOLLOW_UP_NOTE, ISSUE_NOTE, savedText, SIGN_IN_NOTE, type Switch, SWITCH_NAMES } from "./notify";
import { type Saved, saveFailure } from "./save";
import { Status } from "./UserPages";
import { Skeleton } from "./Skeleton";

export function NotificationSettings({ auth, user }: { auth: Auth; user: User }) {
  const [found, setFound] = useState<api.Notifications | "failed" | undefined>(undefined);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then(api.notifications)
      .then((next) => live && setFound(next))
      .catch(() => live && setFound("failed"));
    return () => {
      live = false;
    };
  }, [user, attempt]);
  const change = async (name: Switch, on: boolean) => {
    const before = found;
    if (saving || before === undefined || before === "failed") return;
    const wanted = { ...before, [name]: on };
    setFound(wanted);
    setSaving(true);
    setSaved(null);
    try {
      const next = await api.setNotifications(await user.getIdToken(), wanted);
      setFound(next);
      setSaved({ ok: true, text: savedText(next, name) });
    } catch (e) {
      setFound(before);
      const failed = saveFailure(e, "The switch");
      const kept = `Not saved: the ${SWITCH_NAMES[name]} is still ${before[name] ? "on" : "off"}.`;
      setSaved({ ...failed, text: `${kept} ${failed.text}` });
    } finally {
      setSaving(false);
    }
  };
  return (
    <fieldset data-testid="notification-settings">
      <legend>Notifications</legend>
      {found === undefined ? (
        <Skeleton shape="form" />
      ) : found === "failed" ? (
        <p className="error">
          Cannot load your mail settings.{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : (
        <>
          <label>
            <input
              type="checkbox"
              data-testid="notify-follow-up-mail"
              checked={found.follow_up_mail}
              aria-disabled={saving}
              onChange={(event) => void change("follow_up_mail", event.target.checked)}
            />
            Morning follow-up mail
          </label>
          <p className="hint check-help">{FOLLOW_UP_NOTE}</p>
          <label>
            <input
              type="checkbox"
              data-testid="notify-issue-reminder-mail"
              checked={found.issue_reminder_mail}
              aria-disabled={saving}
              onChange={(event) => void change("issue_reminder_mail", event.target.checked)}
            />
            Issue reminder mail
          </label>
          <p className="hint check-help">{ISSUE_NOTE}</p>
        </>
      )}
      <p className="sub">{SIGN_IN_NOTE}</p>
      <Status saved={saved} auth={auth} testId="notify-status" />
    </fieldset>
  );
}
